import {
    Button,
    cleanObject,
    dispatchCustomEvent,
    FDate,
    MenuProvider,
    Portal,
    Text,
    useDialog,
    useToast,
    View,
} from '@fold-dev/core'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import {
    extractIds,
    findFirstLowerIndentTask,
    findSubtaskCount,
    findTask,
    findTaskByIdFromTasks,
    flattenArray,
    FOLD_TODO_DRAG_TASK_CACHE,
    getCollapsedStateFromLocalStorage,
    LABEL_CREATE_PREFIX,
    SHOULD_DELETE,
    TASK_COLLAPSED,
    Todo,
    TodoProvider,
} from '../../components'
import { processHTML } from '../../components/rich-input/rich-input.util'
import { MAX_TASK_PASTE, POSITION_INCREMENT } from '../../constants'
import {
    getNewPositionsForTasks,
    getPositionIncrement,
    removeExistingElements,
    replaceLastPathValue,
} from '../../helpers'
import { getNewCompletedTaskDate } from '../../helpers/dates'
import { useLabels, useTaskLabels, useTasks, useTaskUsers } from '../../hooks/api.hooks'
import { TaskMenu } from '../../menus/task.menu'
import { useAppStore } from '../../store/app'

export const List = (props) => {
    const { workspaceId, taskId } = useParams()
    const sections = useAppStore((state) => state.sections)
    const projects = useAppStore((state) => state.projects)
    const tasks = useAppStore((state) => state.subtasks)
    const { sort, hideComplete } = useAppStore((state: any) => state.filters)
    const task = useAppStore((state) => state.task)
    const actions = useAppStore((state) => state.actions)
    const {
        id,
        title,
        description,
        priority,
        position,
        complete,
        start,
        end,
        repeat,
        wholeDay,
        users,
        labels,
        projectId,
        projectSectionId,
        projectSectionTitle,
        projectName,
        teamId,
    } = task
    const { setDialog, closeDialog } = useDialog()
    const { showToast } = useToast()
    const taskLabelsAPI = useTaskLabels()
    const tasksAPI = useTasks()
    const labelsAPI = useLabels()
    const navigate = useNavigate()
    const taskUsersAPI = useTaskUsers()
    const { isToday, isUpcoming } = useMemo(() => {
        return {
            isToday: window.location.href.includes('/today'),
            isUpcoming: window.location.href.includes('/upcoming'),
        }
    }, [window.location.href])

    // TaskMenu

    const handleTaskDelete = (task) => {
        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            actions.taskDelete({ task })
                            closeDialog()
                            tasksAPI.remove(workspaceId, projectId, task.id)
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    // TodoProvider

    const handleTaskOpen = (task) => {
        navigate(replaceLastPathValue(window.location.href, task.id))
    }

    const handleTaskUpdate = async (task) => {
        const { id, ...rest } = task
        actions.taskUpdate({ task })
        await tasksAPI.update(workspaceId, projectId, id, { ...rest })
    }

    const handleTaskTitleUpdate = async (taskData) => {
        const { id, index, indent, areaId } = taskData

        let parentTaskId = undefined
        const sectionTasks = flattenArray(tasks)
        const shouldUpdateIndent = indent != undefined

        if (shouldUpdateIndent) {
            const parentTask = findFirstLowerIndentTask(sectionTasks, index, indent)
            parentTaskId = parentTask?.id || null
        }

        const entities = await processHTML(taskData.title)
        const title = entities.title
        const start = entities.dates?.start
        const end = entities.dates?.end
        const repeat = entities.dates?.repeat
        const wholeDay = entities.dates?.wholeDay
        const users = removeExistingElements(entities.users, taskData.users)
        const labels = removeExistingElements(entities.labels, taskData.labels)

        // update task with details from input
        const updateTaskResult = await tasksAPI.updatev2(workspaceId, projectId, id, {
            title,
            start,
            end,
            repeat,
            wholeDay,
            users,
            labels,
            parentTask: { id: parentTaskId },
        })

        // prep the object to update the store
        const updatedTask = cleanObject({
            title,
            start,
            end,
            repeat,
            wholeDay,
            parentTaskId,
            users: [...(taskData.users || []), ...updateTaskResult.users],
            labels: [...(taskData.labels || []), ...updateTaskResult.labels],
            indent: shouldUpdateIndent ? indent : undefined,
        })

        actions.taskUpdate({ task: { id, ...updatedTask } })
    }

    const handleTaskDateUpdate = async ({ id, start, end, repeat, wholeDay }) => {
        actions.taskUpdate({ task: { id, start, end, repeat, wholeDay } })
        await tasksAPI.update(workspaceId, projectId, id, {
            start: start ? FDate(start).toISOWithTimezone() : null,
            end: end ? FDate(end).toISOWithTimezone() : null,
            repeat,
            wholeDay,
        })
    }

    const handleTaskLabelAdd = async ({ id, label }) => {
        const newLabelId = uuidv4()
        const taskLabelId = uuidv4()
        const task = findTask(sections, id)
        const taskId = task.id
        let labels = task.labels || []
        let labelId = label.id
        let newLabel = null

        // create the label if it doesn't exist
        if (!label.id) {
            newLabel = {
                id: newLabelId,
                color: label.color,
                text: label.text.replace(LABEL_CREATE_PREFIX, ''),
                description: '',
            }

            labelId = newLabel.id
            labels.push({ ...newLabel, tlid: taskLabelId })
        } else {
            labels.push({ ...label, tlid: taskLabelId })
        }

        actions.taskUpdate({ task: { id, labels } })

        if (!!newLabel) {
            actions.labelsAdd(newLabel)
            await labelsAPI.create(workspaceId, newLabel)
        }

        await taskLabelsAPI.create(workspaceId, projectId, taskId, { id: taskLabelId, labelId })
    }

    const handleTaskLabelDelete = async ({ id, label }) => {
        const task = findTask(sections, id)
        const taskLabelId = label.tlid
        const taskId = task.id
        const labelId = label.id
        let labels = task.labels || []

        labels = labels.filter((l) => l.id != label.id)

        actions.taskUpdate({ task: { id, labels } })

        await taskLabelsAPI.remove(workspaceId, projectId, taskId, taskLabelId)
    }

    const handleTaskUserAdd = async ({ id, user }) => {
        const taskUserId = uuidv4()
        const task = findTask(tasks, id)
        const taskId = task.id
        const userId = user.id
        let users = task.users || []

        users.push({ ...user, tuid: taskUserId })

        actions.taskUpdate({ task: { id, users } })

        await taskUsersAPI.create(workspaceId, projectId, taskId, { id: taskUserId, userId })
    }

    const handleTaskUserDelete = async ({ id, user }) => {
        const task = findTask(tasks, id)
        const taskUserId = user.tuid
        const taskId = task.id
        const userId = user.id
        let users = task.users || []

        users = users.filter((u) => u.id != user.id)

        actions.taskUpdate({ task: { id, users } })

        await taskUsersAPI.remove(workspaceId, projectId, taskId, taskUserId)
    }

    const handleTaskComplete = async ({ id, complete, start, end, repeat, wholeDay }) => {
        const updatedTask = getNewCompletedTaskDate({ complete, start, end, repeat, wholeDay })

        // update the task right away
        actions.taskUpdate({ task: { id, ...updatedTask } })
        tasksAPI.update(workspaceId, projectId, id, { ...updatedTask })

        // for repeat tasks that have a next date (they don't get marked as checked)
        if (!updatedTask.complete && complete) {
            dispatchCustomEvent('faux-check', { id })
        } else {
            if (hideComplete && updatedTask.complete) {
                dispatchCustomEvent('faux-check', { id })

                // ready it to be removed
                window[SHOULD_DELETE] = [...window[SHOULD_DELETE], id]

                setTimeout(() => {
                    if (window[SHOULD_DELETE].includes(id)) {
                        actions.taskDelete({ task: { id, ...updatedTask } })
                    }
                }, 2000)

                showToast({
                    showProgress: true,
                    delay: 2000,
                    anchor: 'bottom-center',
                    toastComponent: ({ onDismiss }) => (
                        <View
                            row
                            width={200}
                            gap="1rem">
                            <Text
                                flex={1}
                                onClick={onDismiss}
                                colorToken="base-100">
                                Task complete.
                            </Text>
                            <View
                                p="0.5rem 1rem"
                                colorToken="base-100"
                                border="1px solid var(--f-color-surface-strongest)"
                                radius="var(--f-radius)"
                                className="f-buttonize"
                                onClick={() => {
                                    onDismiss()
                                    window[SHOULD_DELETE] = window[SHOULD_DELETE].filter((id1) => id != id1)
                                    actions.taskUpdate({ task: { id, complete: false } })
                                    tasksAPI.update(workspaceId, projectId, id, { id, complete: false })
                                    dispatchCustomEvent('faux-uncheck', { id })
                                }}>
                                Undo?
                            </View>
                        </View>
                    ),
                })
            }
        }
    }

    const handleTaskAdd = async ({ task, sectionIndex }) => {
        if (task.title == '') return

        const lastTask = tasks.length == 0 ? null : tasks[tasks.length - 1]
        const lastTaskId = lastTask ? lastTask.id : null
        const lastTaskPosition = lastTask ? +lastTask.position : +position

        const newPosition = lastTaskPosition + getPositionIncrement()
        const entities = await processHTML(task.title)
        const title = entities.title
        const start = entities.dates?.start
        const end = entities.dates?.end
        const repeat = entities.dates?.repeat
        const wholeDay = entities.dates?.wholeDay
        const labels = entities.labels.map((label) => ({ tlid: uuidv4(), ...label }))
        const users = entities.users.map((user) => ({ tuid: uuidv4(), ...user }))

        const p = projects.find((project) => project.id == projectId)

        // this basically mimics the response structure from the API
        const newTask = {
            id: uuidv4(),
            title,
            description: '',
            priority: null,
            type: null,
            position: newPosition,
            complete: false,
            read: true,
            start,
            end,
            repeat,
            wholeDay,
            externalEmail: null,
            meta: {},
            projectId,
            projectSectionId,
            projectSectionTitle,
            projectName: p?.name,
            projectColor: p?.color,
            teamId,
            comments: [],
            users,
            followers: [],
            labels,
            attachments: [],
        }

        // to the subtasks
        actions.subtaskAdd(newTask)

        // to the project tasks
        actions.taskAddAndRebuild({
            syncWithProjectId: projectId,
            tasks: [{ ...newTask, parentTaskId: id }],
        })

        await tasksAPI.create(
            workspaceId,
            projectId,
            cleanObject({
                id: newTask.id,
                title,
                description: '',
                complete: false,
                read: true,
                position: newPosition,
                projectSection: { id: projectSectionId },
                parentTask: { id },
                users,
                labels,
                start,
                end,
                repeat,
                wholeDay,
            })
        )
    }

    const handleMultipleTaskAdd = async (obj) => {
        const { sectionIndex } = obj
        const multipleTasks = obj.tasks

        if (!Array.isArray(multipleTasks) || multipleTasks.length === 0) return

        if (multipleTasks.length > MAX_TASK_PASTE) {
            alert({
                title: 'Sorry',
                description: `A maximum of ${MAX_TASK_PASTE} tasks are supported at the moment`,
            })
            return
        }

        const lastTask = tasks.length == 0 ? null : tasks[tasks.length - 1]
        const lastTaskPosition = lastTask ? +lastTask.position : +position
        const newTasks = multipleTasks
            .filter(({ title }) => !!title.trim())
            .map(({ title }, index) => ({
                id: uuidv4(),
                read: true,
                title,
                position: lastTaskPosition + getPositionIncrement() * (index + 1),
            }))

        // to the subtasks
        actions.subtaskAddMultiple(newTasks)

        // to the project tasks
        actions.taskAddAndRebuild({
            syncWithProjectId: projectId,
            tasks: newTasks.map((task) => ({ ...task, parentTaskId: id, projectSectionId })),
        })

        await tasksAPI.createMultiple(workspaceId, projectId, projectSectionId, id, newTasks)
    }

    const handleTaskMove = async ({ origin, target, selection }) => {
        const targetIndent = isNaN(+target.indent) ? 0 : +target.indent
        const targetMoveDirection = target.moveDirection
        const targetElementId = target.elementId
        const targetTasks = [window[FOLD_TODO_DRAG_TASK_CACHE]]

        const { id } = task

        const targetTasksFlattened = flattenArray(targetTasks, targetIndent)
        const activeTaskIds = extractIds(targetTasksFlattened)

        // get section where to insert tasks
        const subtasks = flattenArray(tasks).map((task) => ({
            ...task,
            DELETE: activeTaskIds.includes(task.id),
        }))

        const movementAdjuster = targetMoveDirection == 'down' ? 1 : 0
        const targetElementIndex = targetElementId ? subtasks.findIndex((t) => t.id == targetElementId) : 0
        let targetIndex = targetElementIndex + movementAdjuster

        // 🚨
        // if the previous task is collapsed (only possible if it's a parent)
        // then add the child count to the target index so it skips the collapsed children
        if (getCollapsedStateFromLocalStorage(TASK_COLLAPSED, targetElementId) && targetMoveDirection == 'down') {
            targetIndex += findSubtaskCount(subtasks, targetElementId)
        }

        // this gets a series of positions for new tasks (to correctly position them sequentially)
        // also get the parent for the new position
        const parentTask = findFirstLowerIndentTask(subtasks, targetIndex, targetIndent)
        const parentTaskId = parentTask?.id || id
        const positions = getNewPositionsForTasks(subtasks, targetIndex, targetIndent, targetTasksFlattened.length)

        // save these on the object
        const updatedTasks = targetTasksFlattened.map((task, index) => ({
            id: task.id,
            position: +positions[index],
            parentTaskId: task.indent == 0 ? id : task.root ? parentTaskId : task.parentTaskId,
        }))

        actions.subtaskMove({ tasks: updatedTasks })

        tasksAPI.updateMany(
            workspaceId,
            projectId,
            updatedTasks.map(({ id, position, parentTaskId }) => ({
                id,
                position,
                parentTask: { id: parentTaskId },
            }))
        )

        window[FOLD_TODO_DRAG_TASK_CACHE] = null
    }

    return (
        <MenuProvider
            menu={({ data: { target, payload }, dismiss }) => {
                if (!payload) return null

                const taskId = payload.id
                const taskFromSections = findTaskByIdFromTasks([...tasks], taskId)

                return (
                    <TaskMenu
                        showAddAbove={false}
                        showAddBelow={false}
                        type="subtask"
                        task={taskFromSections}
                        onCancel={dismiss}
                        onTodoAddAbove={() => null}
                        onTodoAddBelow={() => null}
                        onSave={(task) => {
                            dismiss()
                            handleTaskUpdate({ id: taskId, ...task })
                        }}
                        onView={() => {
                            dismiss()
                            handleTaskOpen({ ...taskFromSections })
                        }}
                        onDelete={() => {
                            dismiss()
                            handleTaskDelete(taskFromSections)
                        }}
                    />
                )
            }}>
            <TodoProvider
                id="todo-instance"
                onTaskOpen={handleTaskOpen}
                onTaskUpdate={handleTaskUpdate}
                onTaskTitleUpdate={handleTaskTitleUpdate}
                onTaskLabelAdd={handleTaskLabelAdd}
                onTaskLabelDelete={handleTaskLabelDelete}
                onTaskDateUpdate={handleTaskDateUpdate}
                onTaskUserAdd={handleTaskUserAdd}
                onTaskUserDelete={handleTaskUserDelete}
                onTaskComplete={handleTaskComplete}
                onTaskAddBelow={() => null}
                onTaskAddAbove={() => null}
                onTaskAdd={handleTaskAdd}
                onMultipleTaskAdd={handleMultipleTaskAdd}
                onTaskMove={handleTaskMove}
                onTaskMoveToProject={() => null}
                onSectionDelete={() => null}
                onSectionUpdate={() => null}
                onSectionAdd={() => null}
                onSectionMove={() => null}
                noSectionAdd={true}
                noSectionEdit={true}
                noTaskLabels={false}
                noTaskUsers={false}
                noSelection={true}
                noIndent={false}
                noBlankSection={true}
                noDragging={false}
                kanbanMode={false}
                showProjectName={false}>
                <Todo
                    sections={[
                        {
                            id: projectSectionId,
                            title: '',
                            color: undefined,
                            description: '',
                            position: 0,
                            tasks,
                        },
                    ]}
                />
            </TodoProvider>
        </MenuProvider>
    )
}
